import { useMediaQuery, useTheme } from '@mui/material';
import type { GridColumnVisibilityModel, GridRowParams, GridSortModel } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ScrollableDataGrid } from '~/components/data-grid';
import { EmptyState } from '~/components/empty-state';
import { useOrderByParams } from '~/hooks/order-by';
import CreateLayoutIllustration from '~/images/illustrations/create-layout.svg';
import type { LayoutList__Layout as Layout } from '../../queries/list.generated';
import { useColumns } from '../lib';

const NoRowsOverlay = () => {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration={CreateLayoutIllustration}
      header={t('pages.layouts.empty_state.header')}
      description={t('pages.layouts.empty_state.description')}
    />
  );
};

export interface TableProps {
  layouts: readonly Layout[];
  loading: boolean;
}

export const Table = ({ layouts, loading }: TableProps) => {
  const columns = useColumns();
  const [showColumns, setShowColumns] = useState<GridColumnVisibilityModel>({});
  const navigate = useNavigate();

  const theme = useTheme();
  const isSmallAndDown = useMediaQuery(theme.breakpoints.down('sm'));

  const { setOrderByParams, sortModel } = useOrderByParams();

  const onSortModelChange = useCallback(
    (sortModel: GridSortModel) => {
      setOrderByParams(sortModel.at(0) ?? null);
    },
    [setOrderByParams],
  );

  useEffect(() => {
    setShowColumns((x) => ({
      ...x,
      id: !isSmallAndDown,
      kind: !isSmallAndDown,
      width: !isSmallAndDown,
      height: !isSmallAndDown,
      zones: !isSmallAndDown,
    }));
  }, [isSmallAndDown]);

  return (
    <ScrollableDataGrid
      checkboxSelection={false}
      columns={columns}
      columnVisibilityModel={showColumns}
      disableRowSelectionOnClick
      loading={loading}
      onColumnVisibilityModelChange={setShowColumns}
      onRowClick={({ row: layout }: GridRowParams<Layout>) => navigate(`${String(layout.id)}`)}
      rows={layouts}
      slots={{
        columnResizeIcon: () => null,
        noRowsOverlay: NoRowsOverlay,
      }}
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
    />
  );
};
